import type { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { isAdminEmail } from '@/lib/auth/admins';
import { Skeleton } from '@/components/ui/Skeleton';

type RequireAdminProps = {
  children: ReactNode;
};

/**
 * Gate a route on admin allowlist membership. While the session is
 * loading we show a skeleton (same idiom as RequireAuth).
 *
 *  - Anonymous           → redirect to /login?next=<original>
 *  - Signed-in non-admin → in-page "Not authorized" — no redirect, so a
 *                          non-admin who clicks a stale admin link gets a
 *                          clear "you can't see this" rather than a
 *                          confusing bounce.
 *  - Admin               → render children
 */
export function RequireAdmin({ children }: RequireAdminProps) {
  const session = useSession();
  const location = useLocation();

  if (session.isLoading) {
    return (
      <div className="container-prose py-20">
        <Skeleton className="h-6 w-40 mx-auto mb-4" />
        <Skeleton className="h-4 w-64 mx-auto" />
      </div>
    );
  }

  if (!session.data) {
    const nextPath = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(nextPath)}`}
        replace
      />
    );
  }

  if (!isAdminEmail(session.data.user.email)) {
    return (
      <section className="container-prose py-24 text-center">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-rose-400">
          403 · Not Authorized
        </p>
        <h1 className="mb-4 font-display text-3xl font-bold text-night-50">
          Admin access required
        </h1>
        <p className="mx-auto mb-8 max-w-md text-night-200">
          Your account is signed in but isn't on the admin allowlist for this
          page. If you should have access, ask an existing admin to add your
          email to <code className="text-beam">VITE_ADMIN_EMAILS</code>.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-lg border border-beam/40 bg-beam/10 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-beam transition-colors hover:bg-beam/20"
        >
          Back home
        </Link>
      </section>
    );
  }

  return <>{children}</>;
}
